import type { MessageDescriptor } from "@lingui/core";

import { i18n } from "@lingui/core";
import { msg } from "@lingui/core/macro";

import {
  InformedConsent,
  MonitoringSettingsResponse,
  PermissionBit,
} from "../../../api";
import { resolveEnumLabel } from "../../../components/ui-kit/fields/utils";
import {
  informedConsent,
  monitoringTypes,
  patientStatuses,
} from "../../../constants/enum-to-text";
import { AccessRules } from "../../../constants/roles";
import { DataModel } from "../../../models/data-model";

export class MonitoringSettings extends DataModel<MonitoringSettingsItem> {
  readonly idAttr = "uuid" as keyof MonitoringSettingsItem;

  constructor(
    data: MonitoringSettingsItem[],
    permissions: PermissionBit[],
    userUUID: string,
  ) {
    super(data, permissions, userUUID);

    this.access = {
      editRules: { medical: AccessRules.none, monitoring: AccessRules.none },
      deleteRules: { medical: AccessRules.none, monitoring: AccessRules.none },
      historyRequiresMonitoring: false,
    };
  }

  // Base (protected) settings can't be deleted. Returning a message disables
  // the table's delete button and surfaces it as the button's tooltip.
  validateItemDelete(item: MonitoringSettingsItem): string | undefined {
    if (item.is_protected) {
      return i18n._(msg`Base settings - cannot delete`);
    }
    return super.validateItemDelete(item);
  }

  prepareDraft(item: any) {
    const field = super.prepareDraft(item);

    if (item.clinic) {
      field["clinic_uuid"] = item.clinic.uuid;
    }
    if (item.physician) {
      field["physician_uuid"] = item.physician.uuid;
    }

    return field;
  }
}

export function toMonitoringSettingsItem(
  item: MonitoringSettingsResponse,
): MonitoringSettingsItem {
  return {
    ...item,
    monitoringTypeText:
      resolveEnumLabel(monitoringTypes[item.monitoring_type]) ||
      String(item.monitoring_type),
    statusText:
      item.new_patient_status != null
        ? resolveEnumLabel(patientStatuses[item.new_patient_status]) ||
          String(item.new_patient_status)
        : "",
    clinicName: item.clinic?.name ?? "",
    physicianName: item.physician
      ? `${item.physician.given_name} ${item.physician.family_name}`
      : "",
  };
}

export const consentTypeValues: Record<string, MessageDescriptor> = {
  [InformedConsent.telemonitoring]:
    informedConsent[InformedConsent.telemonitoring],
  [InformedConsent.paper]: informedConsent[InformedConsent.paper],
};

export type MonitoringSettingsItem = MonitoringSettingsResponse & {
  monitoringTypeText: string;
  statusText: string;
  clinicName: string;
  physicianName: string;
};
